import { useContext, useEffect, useMemo, useRef, useState, useCallback, memo } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useParams } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import { useQueryClient } from "@tanstack/react-query"
import { MessageCircleCode, Pin, PinOff } from "lucide-react"
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"

// ** Theme Components
import { Button } from "../common/button"
import { Tooltip, TooltipTrigger } from "../common/tooltip"
import { DataTableViewOptions } from "../common/data-table/data-table-view-options"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../common/table"

// ** Api Routes
import {
  CONTRACTOR_APPROVAL_ENDPOINT,
  SERVER_URL,
  SUPERVISOR_APPROVAL_ENDPOINT,
} from "@pma/utils/api-routes"

// ** Types & Constants
import { DictType } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Store
import { useDrawer } from "@pma/stores/drawer-store"

// ** Custom Context
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import WorkDiaryTask from "./work-diary-task"
import ConfirmationMenu from "./confirmation-menu"
import WorkDiaryWeather from "./work-diary-weather"
import WorkDiariesEditor from "./work-diaries-editor"
import WorkDiaryResources from "./work-diary-resources"
import WorkDiariesDetailMenu from "./work-diaries-detail-menu"
import WorkDiaryStatusEditor from "./work-diary-status-editor"
import WorkDiariesColumnHeader from "./work-diaries-column-header"
import { WorkDiariesTablePagination } from "./work-diaries-pagination"

interface WorkDiariesTableProps {
  workDiaries: DictType[]
  isLoading: boolean
  project: DictType
  lstStatus: any[]
  pagination: {
    pageIndex: number
    pageSize: number
  }
  setPagination: (pagination: any) => void
  pageSorting: {
    orderBy: string
    sortBy: string
  }
  setPageSorting: (sortingObj: any) => void
  paginationData: {
    curPage: number
    itemsReceived: number
    itemsTotal: number
    nextPage: number
    offset: number
    pageTotal: number
    prevPage: number
    pageSize: number
  }
}

// Separate cell components to properly use hooks - wrapped in memo for performance
const ContractorApprovalCell = memo(
  ({
    row,
    isSubmitting,
    onApprovalChange,
    translate,
  }: {
    row: any
    isSubmitting: boolean
    onApprovalChange: (
      id: number,
      name: "contractorApproval" | "supervisorApproval",
      value: boolean,
    ) => void
    translate: (key: string) => string
  }) => {
    const { userRoleDetails } = useContext(UserRoleContext)
    const contractor_approval = row.getValue("contractor_approval") as boolean

    const handleConfirm = useCallback(() => {
      onApprovalChange(row.original.id, "contractorApproval", !contractor_approval)
    }, [onApprovalChange, row.original.id, contractor_approval])

    const approvalImage = contractor_approval
      ? `${process.env.PUBLIC_URL}/approved.png`
      : `${process.env.PUBLIC_URL}/disapproved.png`

    return !userRoleDetails?.constructor ? (
      <div className='flex items-center justify-center truncate text-ellipsis'>
        <img src={approvalImage} alt='Brand logo' className='h-[20px] w-[20px] rounded-full' />
      </div>
    ) : (
      <ConfirmationMenu
        onConfirm={handleConfirm}
        isSubmitting={isSubmitting}
        title={translate(`Proceed to change approval?`)}
        message={translate(`Are you sure to want a change contractor approval?`)}
      >
        <div className='flex items-center justify-center truncate text-ellipsis'>
          <img src={approvalImage} alt='Brand logo' className='h-[20px] w-[20px] rounded-full' />
        </div>
      </ConfirmationMenu>
    )
  },
)
ContractorApprovalCell.displayName = "ContractorApprovalCell"

const SupervisorApprovalCell = memo(
  ({
    row,
    isSubmitting,
    onApprovalChange,
    translate,
  }: {
    row: any
    isSubmitting: boolean
    onApprovalChange: (
      id: number,
      name: "contractorApproval" | "supervisorApproval",
      value: boolean,
    ) => void
    translate: (key: string) => string
  }) => {
    const { userRoleDetails } = useContext(UserRoleContext)
    const supervisor_approval = row.getValue("supervisor_approval") as boolean

    const handleConfirm = useCallback(() => {
      onApprovalChange(row.original.id, "supervisorApproval", !supervisor_approval)
    }, [onApprovalChange, row.original.id, supervisor_approval])

    const approvalImage = supervisor_approval
      ? `${process.env.PUBLIC_URL}/approved.png`
      : `${process.env.PUBLIC_URL}/disapproved.png`

    return !userRoleDetails?.supervisor ? (
      <div className='flex justify-center truncate text-ellipsis'>
        <img src={approvalImage} alt='Brand logo' className='h-[20px] w-[20px] rounded-full' />
      </div>
    ) : (
      <ConfirmationMenu
        onConfirm={handleConfirm}
        isSubmitting={isSubmitting}
        title={translate(`Proceed to change approval?`)}
        message={translate(`Are you sure to want a change supervisor approval?`)}
      >
        <div className='flex items-center justify-center truncate text-ellipsis'>
          <img src={approvalImage} alt='Brand logo' className='h-[20px] w-[20px] rounded-full' />
        </div>
      </ConfirmationMenu>
    )
  },
)
SupervisorApprovalCell.displayName = "SupervisorApprovalCell"

const NotesCell = memo(({ row }: { row: any }) => {
  const drawer = useDrawer()
  const notesCount = row?.original?.notes_id?.length || 0
  const diaryId = row.original?.id

  const handleClick = useCallback(() => {
    drawer.onOpen()
    drawer.setContext("word-diary-comment")
    drawer.setWorkDiaryId(diaryId)
    drawer.setWorkDiaryApprovals({
      contractorApproval: !!row.original?.contractor_approval,
      supervisorApproval: !!row.original?.supervisor_approval,
    })
  }, [drawer, diaryId, row.original?.contractor_approval, row.original?.supervisor_approval])

  return (
    <div className='flex items-center truncate text-ellipsis'>
      <div className='relative flex h-full min-h-9 w-[50px] items-center justify-center'>
        <MessageCircleCode
          size={16}
          onClick={handleClick}
          className='cursor-pointer transition-all hover:scale-125'
        />
        {notesCount > 0 && (
          <small className='absolute right-2 top-1 z-10 flex h-3 w-3 items-center justify-center rounded-full bg-primary/80 text-[9px] text-white'>
            {notesCount}
          </small>
        )}
      </div>
    </div>
  )
})
NotesCell.displayName = "NotesCell"

const WorkDiariesTable = ({
  workDiaries,
  isLoading,
  project,
  lstStatus,
  pagination,
  setPagination,
  paginationData,
  setPageSorting,
  pageSorting,
}: WorkDiariesTableProps) => {
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryClient = useQueryClient()
  const paramProjectId = useParams()?.projectId
  const { translate, locale } = useTranslator()
  const loggedInUser = localStorage.getItem("rog_user")
    ? JSON.parse(localStorage?.getItem("rog_user") || "")
    : ""

  const localeRef = useRef(locale)
  const projectRef = useRef(project)
  const lstStatusRef = useRef(lstStatus)
  const translateRef = useRef(translate)
  const paginationRef = useRef(pagination)
  const pageSortingRef = useRef(pageSorting)
  const tableRef = useRef<HTMLTableElement>(null)
  const setPageSortingRef = useRef(setPageSorting)
  const setPaginationRef = useRef(setPagination)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

  // Use refs for values that change but shouldn't trigger column recreation

  // Update refs when values change
  useEffect(() => {
    pageSortingRef.current = pageSorting
    paginationRef.current = pagination
    setPageSortingRef.current = setPageSorting
    setPaginationRef.current = setPagination
    lstStatusRef.current = lstStatus
    projectRef.current = project
    translateRef.current = translate
    localeRef.current = locale
  })

  // Memoize the checkbox change handler
  const handleCheckboxChange = useCallback(
    async (diaryId: number, name: "contractorApproval" | "supervisorApproval", value: boolean) => {
      const endpoint =
        name === "contractorApproval" ? CONTRACTOR_APPROVAL_ENDPOINT : SUPERVISOR_APPROVAL_ENDPOINT

      setIsSubmitting(true)

      try {
        await axios.post(
          SERVER_URL + endpoint,
          { diary_id: diaryId, value },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        )
        await queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
        toast({
          title: translateRef.current("Success"),
          description: name === "contractorApproval"
                  ? translateRef.current("Contractor Approval status change")
                  : translateRef.current("Supervisor Approval status change"),
          variant: "success",
        })
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message
        toast({
          title: translateRef.current("Error"),
          description: errorMessage,
          variant: "error",
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [accessToken, queryClient],
  )

  // Memoize columns - only recreate when truly necessary
  const columns: ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: "_company",
        accessorFn: (row: DictType) => row?._company,
        id: "_company",
        header: ({ column }) => (
          <WorkDiariesColumnHeader
            column={column}
            title='Contractor Company'
            className=''
            pageSorting={pageSortingRef.current}
            pagination={paginationRef.current}
            setPageSorting={setPageSortingRef.current}
            setPagination={setPaginationRef.current}
          />
        ),
        cell: ({ row }) => {
          const contractorCompany: any = row.getValue("_company")
          const name = contractorCompany?.name
          return (
            <div className='flex max-w-40 items-center space-x-2'>
              <img
                alt=''
                src={`${process.env.PUBLIC_URL + "/user-profile.svg"}`}
                className='h-[35px] w-[35px] shrink-0 rounded-full object-cover'
              />
              <div className='flex flex-col overflow-hidden'>
                <span className='truncate font-medium capitalize'>{name || "---"}</span>
              </div>
            </div>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "number",
        header: ({ column }) => (
          <WorkDiariesColumnHeader
            column={column}
            title='Log Number'
            className=''
            pageSorting={pageSortingRef.current}
            pagination={paginationRef.current}
            setPageSorting={setPageSortingRef.current}
            setPagination={setPaginationRef.current}
          />
        ),
        cell: ({ row }) => {
          const workDiary = row.original
          return (
            <div className='items-center'>
              <small
                className={cn(
                  "table-cell truncate text-nowrap",
                  workDiary.deleted ? "text-red-500" : "",
                )}
              >
                {workDiary?.number}
              </small>
            </div>
          )
        },
        enableSorting: true,
      },
      {
        accessorKey: "diary_date",
        header: ({ column }) => (
          <WorkDiariesColumnHeader
            column={column}
            title='Date'
            className=''
            pageSorting={pageSortingRef.current}
            pagination={paginationRef.current}
            setPageSorting={setPageSortingRef.current}
            setPagination={setPaginationRef.current}
          />
        ),
        cell: ({ row }) => {
          const diaryDateRaw = row.getValue("diary_date") as string
          const diary_date =
            localeRef.current === "he" ? diaryDateRaw.split("-").reverse().join("-") : diaryDateRaw
          return (
            <div className='items-center'>
              <small className='table-cell truncate text-nowrap'>{diary_date}</small>
            </div>
          )
        },
        enableSorting: true,
      },
      {
        accessorKey: "contractor_approval",
        header: ({ column }) => (
          <WorkDiariesColumnHeader
            column={column}
            title='Contractor Approval'
            className='w-24'
            pageSorting={pageSortingRef.current}
            pagination={paginationRef.current}
            setPageSorting={setPageSortingRef.current}
            setPagination={setPaginationRef.current}
          />
        ),
        cell: ({ row }) => (
          <ContractorApprovalCell
            row={row}
            isSubmitting={isSubmitting}
            onApprovalChange={handleCheckboxChange}
            translate={translateRef.current}
          />
        ),
        enableSorting: true,
      },
      {
        accessorKey: "supervisor_approval",
        header: ({ column }) => (
          <WorkDiariesColumnHeader
            column={column}
            title='Supervisor Approval'
            className='w-20'
            pageSorting={pageSortingRef.current}
            pagination={paginationRef.current}
            setPageSorting={setPageSortingRef.current}
            setPagination={setPaginationRef.current}
          />
        ),
        cell: ({ row }) => (
          <SupervisorApprovalCell
            row={row}
            isSubmitting={isSubmitting}
            onApprovalChange={handleCheckboxChange}
            translate={translateRef.current}
          />
        ),
        enableSorting: true,
      },
      {
        accessorKey: "address",
        header: ({ column }) => (
          <WorkDiariesColumnHeader
            column={column}
            title='Address'
            className='w-24'
            pageSorting={pageSortingRef.current}
            pagination={paginationRef.current}
            setPageSorting={setPageSortingRef.current}
            setPagination={setPaginationRef.current}
          />
        ),
        cell: ({ row }) => {
          const address = row.getValue("address") as string
          const workDiaryDetails = row?.original
          return (
            <div className='items-center'>
              <WorkDiariesEditor
                id={row.original.id}
                title={"address"}
                value={address}
                workDiaryDetails={workDiaryDetails}
              >
                <p className='mb-0 min-h-6 max-w-[30ch] truncate text-ellipsis text-sm text-slate-500'>
                  {address || "---"}
                </p>
              </WorkDiariesEditor>
            </div>
          )
        },
        enableSorting: true,
      },
      {
        accessorKey: "notes_id",
        header: ({ column }) => (
          <WorkDiariesColumnHeader column={column} title='Comments' className='' />
        ),
        cell: ({ row }) => <NotesCell row={row} />,
        enableSorting: false,
      },
      {
        accessorKey: "weather_id",
        header: ({ column }) => (
          <WorkDiariesColumnHeader column={column} title='Weather' className='' />
        ),
        cell: ({ row }) => {
          const workDiaryDetails = row?.original as DictType
          return <WorkDiaryWeather workDiaryDetails={workDiaryDetails} />
        },
        enableSorting: false,
      },
      {
        accessorKey: "_tasks",
        header: ({ column }) => (
          <WorkDiariesColumnHeader column={column} title='Task' className='' />
        ),
        cell: ({ row }) => {
          const workDiaryDetails = row?.original as DictType
          return <WorkDiaryTask workDiaryDetails={workDiaryDetails} />
        },
        enableSorting: false,
      },
      {
        accessorKey: "_diary_resourses",
        header: ({ column }) => (
          <WorkDiariesColumnHeader column={column} title='Resources' className='!w-16' />
        ),
        cell: ({ row }) => {
          const workDiaryDetails = row?.original as DictType
          return <WorkDiaryResources workDiaryDetails={workDiaryDetails} />
        },
        enableSorting: false,
      },
      {
        accessorFn: (row: DictType) => row?.status_id,
        id: "_status.id",
        header: ({ column }) => (
          <WorkDiariesColumnHeader
            column={column}
            title='Status'
            className='hidden md:block'
            pageSorting={pageSortingRef.current}
            pagination={paginationRef.current}
            setPageSorting={setPageSortingRef.current}
            setPagination={setPaginationRef.current}
          />
        ),
        cell: ({ row }) => {
          const workDiaryDetails = row?.original as DictType
          return (
            <WorkDiaryStatusEditor
              status_id={workDiaryDetails?.status_id}
              lstStatus={lstStatusRef.current}
              workDiaryDetails={workDiaryDetails}
              disabled={true}
            />
          )
        },
        enableSorting: false,
        sortUndefined: "last",
      },
      {
        id: "actions",
        header: ({ column }) => (
          <WorkDiariesColumnHeader column={column} title='Actions' className='w-5' />
        ),
        cell: ({ row }) => (
          <div className='flex w-5 items-center gap-2'>
            <Tooltip>
              <TooltipTrigger>
                <WorkDiariesDetailMenu
                  workDiaries={row?.original}
                  styled={false}
                  project={projectRef.current}
                />
              </TooltipTrigger>
            </Tooltip>
          </div>
        ),
      },
    ],
    // Only truly stable dependencies - isSubmitting and handleCheckboxChange
    [isSubmitting, handleCheckboxChange],
  )

  const getCommonPinningStyles = useCallback((column: Column<DictType>): React.CSSProperties => {
    const isPinned = column.getIsPinned()
    return {
      boxShadow: isPinned ? "-4px 0 4px -4px gray inset" : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? "sticky" : "relative",
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
      background: isPinned ? "white" : undefined,
    }
  }, [])

  const table = useReactTable({
    data: workDiaries,
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    manualSorting: true,
    defaultColumn: {
      size: 150,
      minSize: 50,
      maxSize: 400,
    },
  })

  const handleHeaderPin = useCallback(
    (header: any, pinStatus: boolean | string = false) => {
      const pinnedArr = localStorage.getItem("work-diaries-pinned")
        ? JSON.parse(localStorage.getItem("work-diaries-pinned") || "[]")
        : []

      if (pinnedArr && pinnedArr.length > 0) {
        const currentProject = pinnedArr.find((val: any) => val.projectId === paramProjectId)
        if (currentProject) {
          if (pinStatus === false) {
            // Unpin column
            const index = pinnedArr.indexOf(currentProject)
            const pinArr = currentProject.pinned.filter((val: string) => val !== header.id)
            currentProject["pinned"] = pinArr
            pinnedArr[index] = currentProject

            header.column.pin(false)
            localStorage.setItem("work-diaries-pinned", JSON.stringify(pinnedArr))
          } else {
            // Pin column
            const index = pinnedArr.indexOf(currentProject)
            const pinArr = currentProject.pinned
            pinArr.push(header.id)
            currentProject["pinned"] = pinArr
            pinnedArr[index] = currentProject

            header.column.pin(pinStatus)
            localStorage.setItem("work-diaries-pinned", JSON.stringify(pinnedArr))
          }
        } else {
          // Project not in array yet
          if (pinStatus === "left") {
            const pinArr = [header.id]
            pinnedArr.push({
              projectId: paramProjectId,
              pinned: pinArr,
            })

            header.column.pin(pinStatus)
            localStorage.setItem("work-diaries-pinned", JSON.stringify(pinnedArr))
          }
        }
      } else {
        // No pinned array exists
        if (pinStatus === false) {
          header.column.pin(false)
        } else {
          const pinArr = [header.id]
          pinnedArr.push({
            projectId: paramProjectId,
            pinned: pinArr,
          })

          header.column.pin(pinStatus)
          localStorage.setItem("work-diaries-pinned", JSON.stringify(pinnedArr))
        }
      }
    },
    [paramProjectId],
  )

  useEffect(() => {
    const pinnedArr = localStorage.getItem("work-diaries-pinned")
      ? JSON.parse(localStorage.getItem("work-diaries-pinned") || "[]")
      : []

    if (pinnedArr && pinnedArr.length > 0) {
      const currentProject = pinnedArr.find((val: any) => val.projectId === paramProjectId)
      if (currentProject && currentProject.pinned) {
        const allColumns = table.getAllColumns()
        currentProject.pinned.forEach((pinnedId: string) => {
          const column = allColumns.find((col) => col.id === pinnedId)
          if (column) {
            column.pin("left")
          }
        })
      }
    }
  }, [paramProjectId, table])

  const clearCurrentUserTableFormats = () => {
    const pinnedArr = localStorage.getItem("pinned")
      ? JSON.parse(localStorage?.getItem("pinned") || "")
      : []

    const VisibilityArr = localStorage.getItem("field-visibility")
      ? JSON.parse(localStorage.getItem("field-visibility") || "")
      : []

    const arr = localStorage?.getItem("task-table-order")
      ? JSON.parse(localStorage?.getItem("task-table-order") || "")
      : []

    if (pinnedArr && pinnedArr.length > 0) {
      const found = pinnedArr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email,
      )
      if (found) {
        const index = pinnedArr.indexOf(found)
        found["pinned"] = []
        pinnedArr[index] = found
        localStorage.setItem("pinned", JSON.stringify(pinnedArr))
      }
    }
    if (VisibilityArr && pinnedArr.length > 0) {
      const found = VisibilityArr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email,
      )
      if (found) {
        const index = VisibilityArr.indexOf(found)
        found["field"] = []
        VisibilityArr[index] = found
        localStorage.setItem("field-visibility", JSON.stringify(VisibilityArr))
      }
    }
    if (arr && arr.length > 0) {
      const found = arr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email,
      )
      if (found) {
        const index = arr.indexOf(found)
        arr[index]["order_key"] = ""
        arr[index]["order_value"] = ""
        localStorage.setItem("task-table-order", JSON.stringify(arr))
      }
    }
    window.location.reload()
  }

  return (
    <div>
      <div className='mb-2 flex gap-2'>
        <DataTableViewOptions
          table={table}
          projectId={paramProjectId}
          context={""}
          columns={columns}
        />

        {paramProjectId && (
          <Button
            variant='outline'
            size='sm'
            className='hidden h-8 lg:flex'
            onClick={clearCurrentUserTableFormats}
          >
            {translate("Clear Formats")}
          </Button>
        )}
      </div>
      {!isLoading && workDiaries.length === 0 ? (
        <div className='py-10 text-center text-gray-500'>
          <p className='text-lg font-semibold'>{translate("No work diaries found")}</p>
          <p className='text-sm text-gray-400'>
            {translate("Try adjusting filters or check back later")}.
          </p>
        </div>
      ) : (
        <>
          <div className='overflow-x-auto rounded-md border'>
            <Table ref={tableRef}>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className='sticky top-0 z-[15] bg-white/80 backdrop-blur-md'
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        style={{ ...getCommonPinningStyles(header.column) }}
                        className='whitespace-nowrap'
                      >
                        <div className='flex items-center'>
                          <span className='flex cursor-pointer items-center'>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          <div className='flex items-center'>
                            {header.column.getIsPinned() ? (
                              <PinOff
                                onClick={() => handleHeaderPin(header)}
                                className='m-1 h-3.5 w-3.5 cursor-pointer text-muted-foreground/70 hover:text-primary'
                              />
                            ) : (
                              <Pin
                                onClick={() => handleHeaderPin(header, "left")}
                                className='m-1 h-3.5 w-3.5 cursor-pointer text-muted-foreground/70 hover:text-primary'
                              />
                            )}
                          </div>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow key={`skeleton-`}>
                    {columns.map((_, colIndex) => (
                      <TableCell key={`skeleton-cell-${colIndex}`}>
                        <Skeleton className='h-6 w-full' />
                      </TableCell>
                    ))}
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className={cn(
                        "transition-colors hover:bg-muted/50",
                        row.original?.deleted && "bg-red-50 opacity-60",
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} style={{ ...getCommonPinningStyles(cell.column) }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className='h-24 text-center'>
                      <div className='flex flex-col items-center justify-center text-muted-foreground'>
                        <span className='text-lg'>{translate("No work diaries found")}</span>
                        <span className='text-sm'>
                          {translate("Create a new work diary to get started")}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {paginationData && (
            <WorkDiariesTablePagination
              setPagination={setPagination}
              paginationData={paginationData}
            />
          )}
        </>
      )}
    </div>
  )
}

export default WorkDiariesTable
